import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import sortBy from "lodash/sortBy";
import React, { useCallback } from "react";
import { Alert, Col, Form, FormGroup, Row } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { connect } from "react-redux";
import uuidv4 from "uuid/v4";
import { File } from "../../../docuclipper/DocuclipperTypes";
import { createAlert, CreateAlertFn } from "../../../redux/reducers/Alerts";
import {
  addFile,
  AddFileFn,
  removeFile,
  RemoveFileFn,
} from "../../../redux/reducers/FilesServer";
import Password from "../Passwords";
import TrackedButton from "../TrackedButton";
import DropzoneBtn from "./DropzoneBtn";
import { UploadDetails } from "./UploadDetails";

const UntypedForm = Form as any;

const mime2user = (mime: string) => {
  switch (mime) {
    case "application/pdf":
      return "PDF";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "XLSX";
    case "text/csv":
      return "CSV";
    default:
      return "";
  }
};
type Props = {
  accept: string[];
  multiple: boolean;
  renderNext: () => any;
  btnText?: string;
  uploadedFilesHeadingText?: string;
  onFileAdded?: (file: any) => any;
  dropzoneBtnText?: string;
};

type ReduxProps = {
  addFile: AddFileFn;
  removeFile: RemoveFileFn;
  files: File[];
  createAlert: CreateAlertFn;
};
export const Dropzone3: React.SFC<Props & ReduxProps> = ({
  multiple,
  accept,
  addFile,
  files,
  removeFile,
  renderNext,
  createAlert,
  btnText,
  uploadedFilesHeadingText,
  onFileAdded,
  dropzoneBtnText,
}) => {
  const sortedFiles = sortBy(files, "file.uuid");
  const onDropFn = (files2) => {
    if (!multiple) {
      for (const file of files) {
        removeFile({ file });
      }
    }
    files2.forEach((file: File["file"]) => {
      if (accept.includes(file.type)) {
        file.uuid = uuidv4();

        addFile({
          file,
          isCamera: false,
          asyncProcessing: false,
          customerId: null,
          withCompression: false,
        });
        if (onFileAdded) {
          onFileAdded(file);
        }
      } else {
        createAlert({
          message: `Unsupported file type. Supported types: ${accept
            .map(mime2user)
            .join(", ")}`,
          type: "error",
          timeout: 0,
        });
      }
    });
  };
  const onDrop = useCallback(onDropFn, [files, addFile, multiple, removeFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // accept
  });

  return (
    <>
      <Password />
      <div style={{ maxWidth: "600px" }}>
        <div {...getRootProps()} data-cy="dropzone">
          <UntypedForm.Control {...getInputProps()} />
          <div
            style={{
              border: "#acb4bc dashed 2px",
              borderRadius: "0.3rem",
            }}
          >
            {isDragActive && (
              <Row className="mt-2 justify-content-center">
                <Alert variant="success" className="d-flex  text-center">
                  Drop the files here ...
                </Alert>
              </Row>
            )}
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                height: "100px",
              }}
            >
              <Row>
                <Col>
                  <TrackedButton action="uploadFileClick" variant="secondary">
                    <FontAwesomeIcon icon="upload" />
                    <span className="ml-1">{btnText}</span>
                  </TrackedButton>
                </Col>
              </Row>
            </div>
          </div>
        </div>
        <div className="mt-4">
          {files.length === 0 ? null : (
            <>
              {uploadedFilesHeadingText && (
                <Form.Label>
                  <strong>{`Uploaded file${multiple ? "s" : ""}:`}</strong>
                </Form.Label>
              )}
              {sortedFiles.map((file, i) => (
                <UploadDetails key={i} file={file} removeFile={removeFile} />
              ))}
            </>
          )}
          <FormGroup className="mt-4 d-flex justify-content-end">
            {multiple && (
              <DropzoneBtn onDrop={onDropFn} btnText={dropzoneBtnText} />
            )}
            {renderNext()}
          </FormGroup>
        </div>
      </div>
    </>
  );
};

Dropzone3.defaultProps = {
  btnText: "Upload file(s)",
  uploadedFilesHeadingText: "Uploaded files",
  dropzoneBtnText: "Add more documents",
};

const mapStateToProps = (state) => {
  return {
    files: state.Files.files,
  };
};

export default connect(mapStateToProps, {
  addFile,
  removeFile,
  createAlert,
})(Dropzone3);
